import i18n from '@/i18n';
import { parseAttachments } from '@/components/AttachmentList';
import type { Message } from '@/types/api';

/**
 * Telegram-style one-line preview of the most recent message in a chat /
 * DM list. Pure + total: returns `null` when there is nothing meaningful
 * to show (no message, or a tombstone). Collapses whitespace so multi-line
 * messages don't blow out the row, and falls back to a typed placeholder
 * for media / poll messages that carry no text.
 *
 * `withAuthor` prefixes the sender ("Вы: …" for the current user, otherwise
 * the display name) — used in channels and group DMs where the author isn't
 * implied by the row itself.
 */
export function messagePreviewText(
  msg: Message | null | undefined,
  opts: { selfId?: string | null; withAuthor?: boolean } = {},
): string | null {
  if (!msg || msg.deletedAt) return null;

  let body = (msg.content ?? '').replace(/\s+/g, ' ').trim();

  if (!body) {
    if (msg.poll) {
      body = msg.poll.question
        ? i18n.t('ui.preview.pollWithQuestion', { question: msg.poll.question })
        : i18n.t('ui.preview.poll');
    } else {
      const atts = parseAttachments(msg.attachments);
      const first = atts[0];
      if (first) {
        if (first.durationMs != null) body = i18n.t('ui.preview.voiceMessage');
        else if (first.mime.startsWith('image/'))
          body = i18n.t('ui.preview.photo', { count: atts.length });
        else if (first.mime.startsWith('video/')) body = i18n.t('ui.preview.video');
        else body = i18n.t('ui.preview.attachment', { count: atts.length });
      }
    }
  }

  if (!body) return null;

  if (opts.withAuthor) {
    const isMe = !!opts.selfId && msg.author.id === opts.selfId;
    const name = isMe ? i18n.t('common.you') : msg.author.displayName || msg.author.username;
    return `${name}: ${body}`;
  }
  return body;
}

/**
 * Compact, chat-list timestamp: time-of-day for today, "вчера" for
 * yesterday, the weekday for the last week, else DD.MM. `now` is injectable
 * so the formatting stays pure and testable.
 */
export function shortTimeLabel(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';

  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const dayDiff = Math.round((startOfDay(now) - startOfDay(d)) / 86_400_000);

  if (dayDiff <= 0) {
    return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  }
  if (dayDiff === 1) return i18n.t('common.yesterday');
  if (dayDiff < 7) return d.toLocaleDateString('ru-RU', { weekday: 'short' });
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
}
